import { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import { ROUTES, SIDEBARROUTES } from 'routes/routes'
import {
  Box,
  Icon,
  IconButton,
  ListItemIcon,
  ListItemText,
} from '@mui/material'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import { ReactComponent as Logo } from 'assets/images/logo.svg'
import { useTranslation } from 'react-i18next'
import {
  Language as GlobeIcon,
  LocalMall,
  Search,
  InsertChart,
  Map,
  Menu,
} from '@mui/icons-material'
import i18n from 'lib/i18'
import IconDropdown, {
  DropdownOption,
} from 'components/IconDropdown/IconDropdown'
import { useLocale } from 'providers/LocaleProvider'
import { useAuth } from 'providers/AuthProvider'
import { ReactComponent as Place } from 'assets/images/icon-place.svg'
import { ReactComponent as Product } from 'assets/images/icon-product.svg'
import { TooltipText } from 'components/TooltipText/TooltipText'
import {
  Drawer,
  DrawerHeader,
  SidebarListItem,
  ListButton,
  SidebarHeader,
} from './Sidebar.styled'
import languageOptions from '../../lib/languages'

const sidebarLinksData = {
  ORDER: {
    header: 'fulfilment',
    path: '/order',
    title: 'orders',
    Icon: LocalMall,
  },
  PRODUCT: {
    header: 'commerce',
    path: '/product',
    title: 'product',
    Icon: Product,
  },
  ROUTE: {
    header: '',
    path: '/route',
    title: 'route',
    Icon: Map,
  },
  PLACE: {
    header: 'configuration',
    path: '/place',
    title: 'place',
    Icon: Place,
  },
  SEARCH: {
    header: '',
    path: '/search-configuration',
    title: 'search',
    Icon: Search,
  },
}
type SidebarItemProps = {
  isSidebarOpen: boolean
  toggleSidebar: () => void
}

const SideBarItems = ({ toggleSidebar, isSidebarOpen }: SidebarItemProps) => {
  const { userRoles } = useAuth()
  const { pathname } = useLocation()
  const { t } = useTranslation()

  const linksArray =
    (Object.keys(SIDEBARROUTES).filter((route) => {
      if (route === 'SEARCH' && userRoles?.includes('assortmentadmin')) {
        return 'SEARCH'
      }
      return userRoles?.includes(route.toLowerCase())
    }) as Array<keyof typeof SIDEBARROUTES>) || []

  return (
    <Box sx={{ width: '100%' }} role='presentation'>
      <nav>
        <List>
          <ListItem
            component='div'
            disablePadding
            sx={{
              display: 'block',
              height: '42px',
            }}>
            <Link
              to={ROUTES.DASHBOARD}
              style={{
                textDecoration: 'none',
              }}
              onClick={() => isSidebarOpen && toggleSidebar()}>
              <SidebarListItem
                key={ROUTES.DASHBOARD}
                sx={{ padding: 0, display: 'block', height: '42px' }}>
                <ListButton
                  isSidebarOpen={isSidebarOpen}
                  onClick={() => isSidebarOpen && toggleSidebar()}
                  isSelected={pathname === ROUTES.DASHBOARD}
                  sx={{
                    px: 1,
                    textDecoration: 'none',
                    marginBottom: '10px',
                  }}>
                  <Box
                    display='flex'
                    alignItems='center'
                    sx={{ whiteSpace: 'nowrap' }}>
                    <ListItemIcon>
                      {!isSidebarOpen ? (
                        <TooltipText text={t('dashboard')}>
                          <Icon component={InsertChart} />
                        </TooltipText>
                      ) : (
                        <Icon
                          component={InsertChart}
                          sx={{
                            mr: 1,
                          }}
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText primary={t('dashboard')} />
                  </Box>
                </ListButton>
              </SidebarListItem>
            </Link>
          </ListItem>
          <ListItem component='div' disablePadding sx={{ display: 'block' }}>
            {linksArray.map((item) => {
              const isSelected = pathname
                .split('/')
                .includes(SIDEBARROUTES[item].split('/')[1])
              return (
                <div key={item}>
                  {isSidebarOpen ? (
                    <Box mt={2} ml={3}>
                      <SidebarHeader isSidebarOpen={isSidebarOpen}>
                        {t(sidebarLinksData[item].header)}
                      </SidebarHeader>
                    </Box>
                  ) : (
                    <Box mt={0} ml={1} width='100%' />
                  )}
                  <Link
                    to={SIDEBARROUTES[item]}
                    style={{
                      textDecoration: 'none',
                    }}
                    key={SIDEBARROUTES[item]}
                    onClick={() => isSidebarOpen && toggleSidebar()}>
                    <SidebarListItem
                      key={item}
                      sx={{
                        padding: 0,
                        display: 'block',
                        height: '42px',
                      }}>
                      <ListButton
                        isSidebarOpen={isSidebarOpen}
                        isSelected={isSelected}
                        aria-label={t(`goToLink-Aria`, {
                          link: t(sidebarLinksData[item].title.toLowerCase()),
                        })}
                        sx={{
                          px: 1,
                          marginBottom: '10px',
                        }}>
                        <Box
                          display='flex'
                          alignItems='center'
                          sx={{ whiteSpace: 'nowrap' }}>
                          <ListItemIcon>
                            {!isSidebarOpen ? (
                              <TooltipText
                                text={t(
                                  sidebarLinksData[item].title.toLowerCase()
                                )}>
                                <Icon component={sidebarLinksData[item].Icon} />
                              </TooltipText>
                            ) : (
                              <Icon
                                component={sidebarLinksData[item].Icon}
                                sx={{
                                  mr: 1,
                                }}
                              />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={t(
                              sidebarLinksData[item].title.toLowerCase()
                            )}
                          />
                        </Box>
                      </ListButton>
                    </SidebarListItem>
                  </Link>
                </div>
              )
            })}
          </ListItem>
        </List>
      </nav>
    </Box>
  )
}

const Sidebar = () => {
  const { handleLogout } = useAuth()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen)
  const { t } = useTranslation()
  const { handleLanguageChange, changeLangFormat } = useLocale()

  const userOptions: DropdownOption[] = [
    { label: t('logout'), value: 'logout' },
  ]

  const availableLanguageOptions = languageOptions.map((language) => {
    return { label: t(language.label), value: language.value }
  })

  const handleLanguage = (selectedValue: string) => {
    handleLanguageChange(selectedValue, availableLanguageOptions)
  }

  return (
    <Drawer
      variant='permanent'
      open={isSidebarOpen}
      anchor='left'
      onClose={() => setIsSidebarOpen(false)}>
      <DrawerHeader
        isSidebarOpen={isSidebarOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          minWidth: '24px',
          marginBottom: '54px',
        }}>
        <IconButton
          onClick={toggleSidebar}
          aria-expanded={isSidebarOpen}
          aria-label={t('openSidebar-aria')}
          sx={{
            padding: 0,
            marginLeft: isSidebarOpen ? '23px' : '0px',
            transition: '0.2s ease-in-out margin-left',
          }}>
          {isSidebarOpen ? <Icon component={Logo} /> : <Menu />}
        </IconButton>
        {isSidebarOpen && (
          <Box
            justifyContent='space-between'
            display='flex'
            sx={{ margin: '15px' }}>
            <IconDropdown
              minWidth='150px'
              expandIconRequired
              translateLabel
              Icon={GlobeIcon}
              isIconOnly
              optionItem={changeLangFormat(
                i18n.language,
                availableLanguageOptions
              )}
              ariaLabel={t('languageSelection-Aria')}
              options={availableLanguageOptions}
              onChange={handleLanguage}
            />
            <IconDropdown
              minWidth='150px'
              expandIconRequired
              Icon={PersonOutlineIcon}
              isIconOnly
              ariaLabel={t('userActions-Aria')}
              options={userOptions}
              onChange={handleLogout}
            />
          </Box>
        )}
      </DrawerHeader>
      <SideBarItems
        toggleSidebar={toggleSidebar}
        isSidebarOpen={isSidebarOpen}
      />
    </Drawer>
  )
}

export default Sidebar
